import SmallPopupDefaultTemplate from "components/templates/modal/small/SmallPopupDefaultTemplate";
import PrimaryButton from "components/atoms/buttons/PrimaryButton";
import { FiCamera } from "react-icons/fi";
import * as s from "../EditRecorderStyled";
import { FaFileCirclePlus } from "react-icons/fa6";
import { MdAutoAwesome } from "react-icons/md";
import CameraThumbnailModal from "./CameraThumbnailModal";
import { useState } from "react";
import { IoChevronBackOutline } from "react-icons/io5";
import BigButton from "components/atoms/buttons/BigButton";
import ThumbnailImageGrid from "./ThumbnailImageGrid";
import MYDW_LOGO from "assets/icons/mydw-logo.png";
import COVA_LOGO from "assets/icons/cova-logo.svg";
import DW_CLOUD from "assets/images/dw_cloud_logo_b.png";
import SPECTRUM_LOGO from "assets/images/dw_spectrum_logo.png";
import SPECTRUM_VERTICAL from "assets/images/spectrum-vertical.png";
import VMAX_LOGO from "assets/images/vmax.png";
import VMAX_BLACK from "assets/images/VMAX_black.png";
import DW_CLOUD_IMAGE from "assets/images/DW_Cloud.png";

export enum ThumbnailModalType {
  SELECT = "select",
  UPLOAD = "upload",
  CAMERA = "camera",
  SUGGESTED = "suggested",
}

interface Props {
  type: ThumbnailModalType | null;
  imgFile: string;
  onClose: () => void;
  onSelectType: (type: ThumbnailModalType) => void;
  onUploadFromDevice: () => void;
}

export default function ThumbnailChangeModal({
  type,
  imgFile,
  onClose,
  onSelectType,
  onUploadFromDevice,
}: Props): JSX.Element | null {
  const [cameraKeyword, setCameraKeyword] = useState<string>("");
  const [selectedCameraId, setSelectedCameraId] = useState<string>("cam-02");
  const [selectedSuggestedId, setSelectedSuggestedId] =
    useState<string>("suggest-02");
  if (!type) return null;

  const actionItems = [
    {
      type: ThumbnailModalType.UPLOAD,
      icon: <FaFileCirclePlus size={26} />,
      label: (
        <>
          Upload Image
          <br />
          on this device
        </>
      ),
    },
    {
      type: ThumbnailModalType.CAMERA,
      icon: <FiCamera size={26} />,
      label: (
        <>
          Camera
          <br />
          Thumbnail
        </>
      ),
    },
    {
      type: ThumbnailModalType.SUGGESTED,
      icon: <MdAutoAwesome size={26} />,
      label: (
        <>
          Suggested
          <br />
          Image
        </>
      ),
    },
  ] as const;

  const modalConfigByType: Record<
    ThumbnailModalType,
    {
      title: string;
      subTitle?: string;
      buttons: JSX.Element;
      body: JSX.Element;
    }
  > = {
    [ThumbnailModalType.SELECT]: {
      title: "Change Recorder Thumbnail",
      subTitle: "Upload an image file or select a thumbnail from a camera.",
      buttons: (
        <PrimaryButton
          buttonType="cancel"
          label="Close"
          onClickBtn={onClose}
          data-testid="recorders-edit-thumbnail-primary-button-close"
        />
      ),

      body: (
        <>
          <s.ThumbPreviewBox>
            {imgFile !== "" ? (
              <img src={imgFile} alt="Recorder_Thumbnail_Preview" />
            ) : (
              <s.ThumbPreviewEmpty>No image selected</s.ThumbPreviewEmpty>
            )}
          </s.ThumbPreviewBox>

          <s.ThumbActionGrid>
            {actionItems.map((item) => (
              <s.ThumbActionItem
                key={item.type}
                type="button"
                onClick={() => onSelectType(item.type)}
                data-testid="recorders-edit-thumbnail-thumb-action-item-select-type"
              >
                <s.ThumbActionIconBox>{item.icon}</s.ThumbActionIconBox>
                <s.ThumbActionLabel>{item.label}</s.ThumbActionLabel>
              </s.ThumbActionItem>
            ))}
          </s.ThumbActionGrid>
        </>
      ),
    },
    [ThumbnailModalType.UPLOAD]: {
      title: "Upload Image",
      subTitle:
        "Select an image file from your device as the recorder thumbnail.",
      buttons: (
        <PrimaryButton
          label="Save"
          onClickBtn={onClose}
          disabled={!imgFile}
          data-testid="recorders-edit-thumbnail-primary-button-close-2"
        />
      ),

      body: (
        <s.UploadBody>
          <s.ThumbPreviewBox>
            {imgFile !== "" ? (
              <img src={imgFile} alt="Uploaded_Recorder_Thumbnail" />
            ) : (
              <s.ThumbPreviewEmpty>No image selected</s.ThumbPreviewEmpty>
            )}
          </s.ThumbPreviewBox>
          <s.UploadActionRow>
            <s.ThumbModalInfoText>
              Supports: PNG, JPG, JPEG (max 2MB)
            </s.ThumbModalInfoText>
            <BigButton
              buttonType="cancel"
              label={imgFile !== "" ? "Upload Again" : "Upload Image"}
              onClickBtn={onUploadFromDevice}
              data-testid="recorders-edit-thumbnail-button-upload-from-device"
            />
          </s.UploadActionRow>
        </s.UploadBody>
      ),
    },
    [ThumbnailModalType.CAMERA]: {
      title: "Camera Thumbnail",
      buttons: (
        <PrimaryButton
          label="Save"
          onClickBtn={onClose}
          disabled={!selectedCameraId}
          data-testid="recorders-edit-thumbnail-primary-button-close-3"
        />
      ),

      body: (
        <CameraThumbnailModal
          cameraKeyword={cameraKeyword}
          selectedCameraId={selectedCameraId}
          onChangeCameraKeyword={setCameraKeyword}
          onSelectCamera={setSelectedCameraId}
        />
      ),
    },
    [ThumbnailModalType.SUGGESTED]: {
      title: "Suggested Image",
      buttons: (
        <PrimaryButton
          label="Save"
          onClickBtn={onClose}
          disabled={!selectedSuggestedId}
          data-testid="recorders-edit-thumbnail-primary-button-close-4"
        />
      ),

      body: (
        <ThumbnailImageGrid
          items={[
            { id: "suggest-01", name: "myDW", imageUrl: MYDW_LOGO },
            { id: "suggest-02", name: "COVA", imageUrl: COVA_LOGO },
            { id: "suggest-03", name: "DW Cloud", imageUrl: DW_CLOUD },
            {
              id: "suggest-04",
              name: "DW Spectrum",
              imageUrl: SPECTRUM_LOGO,
            },
            {
              id: "suggest-05",
              name: "Spectrum Vertical",
              imageUrl: SPECTRUM_VERTICAL,
            },
            { id: "suggest-06", name: "VMAX", imageUrl: VMAX_LOGO },
            { id: "suggest-07", name: "VMAX Black", imageUrl: VMAX_BLACK },
            { id: "suggest-08", name: "DW Cloud", imageUrl: DW_CLOUD_IMAGE },
          ]}
          selectedId={selectedSuggestedId}
          onSelect={setSelectedSuggestedId}
          imageFit="contain"
        />
      ),
    },
  };

  const config = modalConfigByType[type];
  const onClickBack =
    type === ThumbnailModalType.SELECT
      ? onClose
      : () => onSelectType(ThumbnailModalType.SELECT);
  return (
    <SmallPopupDefaultTemplate onModal={onClose} button={config.buttons}>
      <s.ModalHeader>
        {type !== ThumbnailModalType.SELECT && (
          <s.ModalBackButton
            type="button"
            onClick={onClickBack}
            data-testid="recorders-edit-thumbnail-modal-back-button-click-back"
          >
            <IoChevronBackOutline size={20} />
          </s.ModalBackButton>
        )}

        <s.ModalHeaderText>
          <s.ModalTitle>{config.title}</s.ModalTitle>
          {config.subTitle && (
            <s.ModalSubTitle>{config.subTitle}</s.ModalSubTitle>
          )}
        </s.ModalHeaderText>
      </s.ModalHeader>
      <s.ThumbnailModalBody>{config.body}</s.ThumbnailModalBody>
    </SmallPopupDefaultTemplate>
  );
}
